import { useState, useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { AlertProvider } from "./contexts/AlertContext";
import { AuthProvider } from "./contexts/AuthContext";
import { WarehouseProvider } from "./contexts/WarehouseContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box } from "@mui/material";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import NavBar from "./components/NavBar/NavBar";
import SideBar from "./components/SideBar/SideBar";
import Products from "./pages/Products/Products";
import AdminPanel from "./pages/AdminPanel/AdminPanel";
import Clients from "./pages/Clients/Clients";
import Shipments from "./pages/Shipments/Shipments";
import Login from "./pages/Login/Login";
import ProductDetails from "./components/ProductsDetails.tsx/ProductDetails";
import Breadcrumbs from "./components/Breadcrumbs/Breadcrumbs";
import Documents from "./pages/Documents/Documents";
import CreateDocument from "./pages/Documents/CreateDocument";
import DocumentDetails from "./components/DocumentDetails/DocumentDetails";
import Labels from "./pages/Labels/Labels";
import Dashboard from "./pages/Dashboard/Dashboard";
import WarehouseMap from "./pages/WarehouseMap/WarehouseMap";
import Reports from "./pages/Reports/Reports";
import Chat from "./pages/Chat/Chat";
import Notifications from "./pages/Notifications/Notifications";
import VoicePanel from "./components/VoicePanel/VoicePanel";
import Bundles from "./pages/Bundles/Bundles";
import PrivateRoute from "./components/PrivateRoute/PrivateRoute";
import AdminRoute from "./components/AdminRoute/AdminRoute";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Primary dobrane do #00a98c, hex dla MUI
const mint = {
  main: "#00a98c",
  light: "#33baa3",
  dark: "#008870",
  contrastText: "#ffffff",
};

const theme = createTheme({
  palette: {
    primary: mint,
    secondary: {
      main: "#66c9b8",
      light: "#b3e4db",
      dark: "#00a98c",
      contrastText: "#005a4d",
    },
    info: { main: mint.main, light: mint.light, dark: mint.dark },
    success: { main: mint.main, light: mint.light, dark: mint.dark },
    warning: { main: mint.light, light: "#9ee0d0", dark: mint.main },
    error: { main: mint.dark, light: mint.main, dark: "#2d7a6e" },
    text: {
      primary: "#ffffff",
      secondary: "rgba(255, 255, 255, 0.7)",
    },
    background: {
      default: "#18181a",
      paper: "#46464644",
    },
  },
  components: {
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "rgba(97, 97, 97, 0.08)",
            "&:hover": {
              backgroundColor: "rgba(97, 97, 97, 0.12)",
            },
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#2a2a2a",
          backgroundImage: "none",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "#353535",
          },
          "&.Mui-selected": {
            backgroundColor: "#404040",
            "&:hover": {
              backgroundColor: "#454545",
            },
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&:-webkit-autofill": {
            WebkitBoxShadow: "0 0 0 100px #111111a1 inset",
            WebkitTextFillColor: "#ffffff",
          },
          "&:-webkit-autofill:hover": {
            WebkitBoxShadow: "0 0 0 100px #111111a1 inset",
            WebkitTextFillColor: "#ffffff",
          },
          "&:-webkit-autofill:focus": {
            WebkitBoxShadow: "0 0 0 100px #111111a1 inset",
            WebkitTextFillColor: "#ffffff",
          },
        },
      },
    },
  },
});

const MainLayout = () => {
  const location = useLocation();
  const isLoginPage = location.pathname === "/login";
  const drawerWidth = 240;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  if (isLoginPage) {
    return <Login />;
  }

  return (
    <PrivateRoute>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <NavBar
          drawerWidth={drawerWidth}
          handleDrawerToggle={handleDrawerToggle}
          onOpenVoicePanel={() => setVoicePanelOpen(true)}
        />
        <VoicePanel
          open={voicePanelOpen}
          onClose={() => setVoicePanelOpen(false)}
        />
        <SideBar
          mobileOpen={mobileOpen}
          handleDrawerToggle={handleDrawerToggle}
        />
        <Box
          component="main"
          onClick={() => {
            if (window.innerWidth < 600) {
              setMobileOpen(false);
            }
          }}
          sx={{
            flexGrow: 1,
            pl: { xs: 2, sm: 3 },
            pr: { xs: 2, sm: 3 },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minHeight: "calc(100vh - 64px)",
            mt: { xs: "56px", sm: "64px" },
            transition: "margin 0.3s ease",
            overflow: "hidden",
            maxWidth: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Breadcrumbs />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/documents/create" element={<CreateDocument />} />
            <Route path="/documents/edit/:id" element={<CreateDocument />} />
            <Route path="/documents/:id" element={<DocumentDetails />} />
            <Route path="/shipments" element={<Shipments />} />
            <Route path="/bundles" element={<Bundles />} />
            <Route path="/warehouse-map" element={<WarehouseMap />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/admin-panel" element={<AdminRoute><AdminPanel /></AdminRoute>} />
          </Routes>
        </Box>
      </Box>
    </PrivateRoute>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AlertProvider>
            <AuthProvider>
              <WarehouseProvider>
                <Router>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/*" element={<MainLayout />} />
                  </Routes>
                </Router>
              </WarehouseProvider>
            </AuthProvider>
          </AlertProvider>
        </ThemeProvider>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
